import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { concatLatestFrom } from '@ngrx/operators';
import { BehaviorSubject, map, Observable, startWith } from 'rxjs';
import { ControlValueAccessorConnector } from '../utils/control-value-accessor-connector/control-value-accessor-connector';
import { AutoCompleteOption } from './auto-complete-option';

@Component({
  selector: 'stena-auto-complete',
  templateUrl: './stena-auto-complete.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatInputModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StenaAutoCompleteComponent),
      multi: true,
    },
  ],
})
export class StenaAutoCompleteComponent
  extends ControlValueAccessorConnector
  implements OnInit
{
  @Input() label?: string;
  @Input() placeholder = '';
  @Input() set autoCompleteOptions(options: AutoCompleteOption[]) {
    this.options$.next(options);
  }
  @Input() addValidation = true;
  @Input() wideInput = false;
  filteredOptions$!: Observable<AutoCompleteOption[]>;
  options$ = new BehaviorSubject<AutoCompleteOption[]>([]);

  displayFn(value: number): string {
    return !value
      ? ''
      : (this.options$.value.find((a) => a.value === value) || {})
          ?.displayName || '';
  }

  ngOnInit(): void {
    if (this.addValidation) {
      this.control.addValidators(
        (control: AbstractControl): ValidationErrors | null => {
          const valid =
            (this.options$.value || []).findIndex(
              (a) => a.value === control.value || !control.value
            ) > -1;
          return valid
            ? null
            : { autoCompleteOptionInvalid: { value: control.value } };
        }
      );
    }

    this.filteredOptions$ = this.control.valueChanges.pipe(
      startWith(''),
      concatLatestFrom(() => this.options$),
      map(([searchVal, options]) => {
        return !searchVal
          ? options
          : options.filter(
              (op) =>
                op.displayName
                  .toLowerCase()
                  .indexOf(searchVal.toString().toLowerCase()) > -1
            );
      })
    );
  }

  trackOptionBy(index: number, option: AutoCompleteOption): number | string {
    return option.value;
  }
}
